// Tool input schemas — one row per field. Used by Tools.js to render forms dynamically.
// Field types: number | text | select | array (table with sub-fields)

export const TOOL_SCHEMAS = {
  ind_as_116_lease: [
    { key: "lease_term_months", label: "Lease Term (months)", type: "number", default: 60 },
    { key: "monthly_rent", label: "Monthly Rent (₹)", type: "number", default: 100000 },
    { key: "escalation_pct", label: "Escalation %", type: "number", default: 5 },
    { key: "escalation_freq_months", label: "Escalation Frequency (months)", type: "number", default: 12 },
    { key: "incremental_borrowing_rate", label: "Incremental Borrowing Rate (% p.a.)", type: "number", default: 9 },
    { key: "initial_direct_costs", label: "Initial Direct Costs", type: "number", default: 0 },
    { key: "lease_incentives", label: "Lease Incentives", type: "number", default: 0 },
    { key: "restoration_obligation", label: "Restoration Obligation", type: "number", default: 0 },
    { key: "payment_at_period_start", label: "Payment at Period Start", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
  ],
  ecl_simplified: [
    { key: "buckets", label: "Ageing Buckets", type: "array", default: [
      { name: "Current", amount: 0, default_rate: 0.5 },
      { name: "1–30", amount: 0, default_rate: 1 },
      { name: "31–60", amount: 0, default_rate: 3 },
      { name: "61–90", amount: 0, default_rate: 8 },
      { name: ">90", amount: 0, default_rate: 20 },
    ], columns: [
      { key: "name", label: "Bucket", type: "text" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "default_rate", label: "Default Rate %", type: "number" },
    ]},
  ],
  ecl_pd_lgd_ead: [
    { key: "items", label: "Exposures", type: "array", default: [{ name: "Loan A", pd: 5, lgd: 60, ead: 1000000 }], columns: [
      { key: "name", label: "Name", type: "text" },
      { key: "pd", label: "PD %", type: "number" },
      { key: "lgd", label: "LGD %", type: "number" },
      { key: "ead", label: "EAD", type: "number" },
    ]},
  ],
  dcf: [
    { key: "cash_flows", label: "Free Cash Flows (₹) — comma separated for each year", type: "csv_numbers", default: "1000,1100,1210,1331,1464" },
    { key: "wacc", label: "WACC %", type: "number", default: 12 },
    { key: "terminal_growth", label: "Terminal Growth %", type: "number", default: 3 },
    { key: "net_debt", label: "Net Debt", type: "number", default: 0 },
    { key: "shares_outstanding", label: "Shares Outstanding", type: "number", default: 0 },
  ],
  wacc: [
    { key: "risk_free_rate", label: "Risk-Free Rate %", type: "number", default: 7 },
    { key: "beta", label: "Beta", type: "number", default: 1 },
    { key: "market_premium", label: "Market Premium %", type: "number", default: 6 },
    { key: "cost_of_debt", label: "Cost of Debt %", type: "number", default: 9 },
    { key: "tax_rate", label: "Tax Rate %", type: "number", default: 25.17 },
    { key: "weight_equity", label: "Weight of Equity %", type: "number", default: 60 },
    { key: "weight_debt", label: "Weight of Debt %", type: "number", default: 40 },
  ],
  materiality: [
    { key: "revenue", label: "Revenue", type: "number", default: 0 },
    { key: "pbt", label: "PBT", type: "number", default: 0 },
    { key: "total_assets", label: "Total Assets", type: "number", default: 0 },
    { key: "equity", label: "Equity", type: "number", default: 0 },
  ],
  deferred_tax: [
    { key: "tax_rate", label: "Tax Rate %", type: "number", default: 25.17 },
    { key: "items", label: "Items", type: "array", default: [{ description: "PPE", book_value: 0, tax_value: 0 }], columns: [
      { key: "description", label: "Description", type: "text" },
      { key: "book_value", label: "Book Value", type: "number" },
      { key: "tax_value", label: "Tax Value", type: "number" },
    ]},
  ],
  ppa_ind_as_103: [
    { key: "purchase_consideration", label: "Purchase Consideration", type: "number", default: 0 },
    { key: "nci_value", label: "NCI Value", type: "number", default: 0 },
    { key: "fv_net_assets_acquired", label: "FV of Net Assets Acquired", type: "number", default: 0 },
    { key: "intangibles", label: "Intangibles", type: "array", default: [{ name: "Customer Relationships", fv: 0 }], columns: [
      { key: "name", label: "Name", type: "text" },
      { key: "fv", label: "Fair Value", type: "number" },
    ]},
  ],
  goodwill_calculator: [
    { key: "purchase_consideration", label: "Purchase Consideration", type: "number", default: 0 },
    { key: "nci_value", label: "NCI Value", type: "number", default: 0 },
    { key: "fv_net_assets_acquired", label: "FV of Net Assets Acquired", type: "number", default: 0 },
  ],
  impairment_ind_as_36: [
    { key: "cash_flows", label: "Forecast Cash Flows", type: "csv_numbers", default: "1000,1100,1200,1300,1400" },
    { key: "wacc", label: "Discount Rate %", type: "number", default: 12 },
    { key: "fair_value_less_cost_to_sell", label: "Fair Value - Cost to Sell", type: "number", default: 0 },
    { key: "carrying_amount", label: "Carrying Amount", type: "number", default: 5000 },
  ],
  revenue_recognition_115: [
    { key: "contract_value", label: "Contract Value", type: "number", default: 1000000 },
    { key: "total_estimated_cost", label: "Total Estimated Cost", type: "number", default: 800000 },
    { key: "cost_incurred_to_date", label: "Cost Incurred to Date", type: "number", default: 200000 },
    { key: "billed_to_date", label: "Billed to Date", type: "number", default: 250000 },
  ],
  percentage_completion: [
    { key: "contract_value", label: "Contract Value", type: "number", default: 1000000 },
    { key: "total_estimated_cost", label: "Total Estimated Cost", type: "number", default: 800000 },
    { key: "cost_incurred_to_date", label: "Cost Incurred", type: "number", default: 200000 },
    { key: "billed_to_date", label: "Billed", type: "number", default: 250000 },
  ],
  security_deposit_fv: [
    { key: "deposit_amount", label: "Deposit Amount", type: "number", default: 100000 },
    { key: "tenure_years", label: "Tenure (years)", type: "number", default: 5 },
    { key: "market_rate", label: "Market Rate %", type: "number", default: 8 },
  ],
  financial_guarantee: [
    { key: "guarantee_amount", label: "Guarantee Amount", type: "number", default: 10000000 },
    { key: "market_fee_pct", label: "Market Fee %", type: "number", default: 1.5 },
    { key: "tenure_years", label: "Tenure (years)", type: "number", default: 3 },
  ],
  lease_vs_buy: [
    { key: "asset_cost", label: "Asset Cost", type: "number", default: 1000000 },
    { key: "monthly_lease", label: "Monthly Lease", type: "number", default: 20000 },
    { key: "loan_rate", label: "Loan Rate %", type: "number", default: 10 },
    { key: "useful_life_months", label: "Useful Life (months)", type: "number", default: 60 },
    { key: "residual_value", label: "Residual Value", type: "number", default: 0 },
  ],
  sampling: [
    { key: "population", label: "Population", type: "number", default: 1000 },
    { key: "confidence_level", label: "Confidence Level %", type: "select", options: [[90,"90"], [95,"95"], [99,"99"]], default: 95 },
    { key: "expected_error_rate", label: "Expected Error Rate %", type: "number", default: 2 },
    { key: "tolerable_error_rate", label: "Tolerable Error Rate %", type: "number", default: 5 },
  ],
  tax_provision: [
    { key: "pbt", label: "Profit Before Tax (as per books)", type: "number", default: 10000000 },
    { key: "regime", label: "Tax Regime", type: "select", options: [["new","New (22% u/s 115BAA)"], ["old","Old (30%)"], ["old_115bab","New Mfg (15% u/s 115BAB)"]], default: "new" },
    { key: "tax_rate", label: "Override Base Rate % (blank = use regime default)", type: "number", default: 0 },
    // Permanent additions
    { key: "book_depreciation", label: "Depreciation as per Books (add-back)", type: "number", default: 0 },
    { key: "csr_expense", label: "CSR expense (non-80G)", type: "number", default: 0 },
    { key: "penalties", label: "Fines / Penalties (disallowed)", type: "number", default: 0 },
    { key: "tds_disallowance", label: "TDS not deducted — s.40(a)(ia)", type: "number", default: 0 },
    { key: "s43b_unpaid", label: "Statutory dues unpaid — s.43B", type: "number", default: 0 },
    { key: "other_disallowances", label: "Other disallowances", type: "number", default: 0 },
    // Permanent deductions
    { key: "it_depreciation", label: "Depreciation as per IT Act", type: "number", default: 0 },
    { key: "exempt_income", label: "Exempt income (s.10, s.10AA)", type: "number", default: 0 },
    { key: "d_80g", label: "80G Donations", type: "number", default: 0 },
    { key: "d_80jjaa", label: "80JJAA (New Employment)", type: "number", default: 0 },
    { key: "d_80m", label: "80M (Inter-corporate dividend)", type: "number", default: 0 },
    { key: "bf_losses", label: "Brought-forward losses set-off", type: "number", default: 0 },
    { key: "unabs_dep", label: "Unabsorbed depreciation set-off", type: "number", default: 0 },
    // MAT
    { key: "mat_book_profit", label: "MAT Book Profit u/s 115JB (0 if new regime)", type: "number", default: 0 },
    // Credits
    { key: "tds_credit", label: "TDS/TCS credit (Form 26AS)", type: "number", default: 0 },
    { key: "advance_tax_paid", label: "Advance tax paid", type: "number", default: 0 },
    { key: "self_assessment_paid", label: "Self-assessment tax paid", type: "number", default: 0 },
  ],
  rule_11ua: [
    { key: "paid_up_capital", label: "Paid-up Capital", type: "number", default: 0 },
    { key: "reserves_surplus", label: "Reserves & Surplus", type: "number", default: 0 },
    { key: "revaluation_reserve", label: "Revaluation Reserve", type: "number", default: 0 },
    { key: "accumulated_losses", label: "Accumulated Losses", type: "number", default: 0 },
    { key: "no_of_shares", label: "No. of Shares", type: "number", default: 1 },
    { key: "book_value_assets", label: "Book Value of Assets", type: "number", default: 0 },
    { key: "book_value_liabilities", label: "Book Value of Liabilities", type: "number", default: 0 },
    { key: "fv_assets", label: "FV of Assets", type: "number", default: 0 },
  ],
  caro_applicability: [
    { key: "paid_up_capital", label: "Paid-up Capital (₹)", type: "number", default: 0 },
    { key: "reserves_surplus", label: "Reserves & Surplus (₹)", type: "number", default: 0 },
    { key: "turnover", label: "Total Revenue (₹)", type: "number", default: 0 },
    { key: "borrowings", label: "Max Borrowings during year — banks/FIs (₹)", type: "number", default: 0 },
    { key: "is_private_company", label: "Private Co.", type: "select", options: [["true","Yes"], ["false","No"]], default: "true" },
    { key: "is_holding_or_subsidiary_of_public", label: "Holding / Subsidiary of a Public Co.", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
    { key: "is_listed", label: "Listed", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
    { key: "is_one_person_co", label: "OPC", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
    { key: "is_insurance_or_banking", label: "Insurance/Banking", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
    { key: "is_section_8", label: "Section 8", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
  ],
  csr_applicability: [
    { key: "networth", label: "Net Worth", type: "number", default: 0 },
    { key: "turnover", label: "Turnover", type: "number", default: 0 },
    { key: "net_profit_3yr_avg", label: "3-Year Avg Net Profit", type: "number", default: 0 },
    { key: "net_profit_immediately_preceding", label: "Net Profit (Preceding Year)", type: "number", default: 0 },
  ],
  mat_credit: [
    { key: "opening_balance", label: "Opening Balance", type: "number", default: 0 },
    { key: "mat_paid_current_year", label: "MAT Paid (Current Year)", type: "number", default: 0 },
    { key: "utilised_current_year", label: "Utilised (Current Year)", type: "number", default: 0 },
  ],
  esop_black_scholes: [
    { key: "stock_price", label: "Stock Price (S)", type: "number", default: 100 },
    { key: "strike_price", label: "Strike (K)", type: "number", default: 100 },
    { key: "time_to_maturity_years", label: "Time to Maturity (years)", type: "number", default: 4 },
    { key: "risk_free_rate", label: "Risk-Free Rate %", type: "number", default: 7 },
    { key: "volatility_pct", label: "Volatility %", type: "number", default: 30 },
    { key: "dividend_yield_pct", label: "Dividend Yield %", type: "number", default: 0 },
  ],
  esop_binomial: [
    { key: "stock_price", label: "Stock Price (S)", type: "number", default: 100 },
    { key: "strike_price", label: "Strike (K)", type: "number", default: 100 },
    { key: "time_to_maturity_years", label: "Time to Maturity (years)", type: "number", default: 4 },
    { key: "risk_free_rate", label: "Risk-Free Rate %", type: "number", default: 7 },
    { key: "volatility_pct", label: "Volatility %", type: "number", default: 30 },
    { key: "dividend_yield_pct", label: "Dividend Yield %", type: "number", default: 0 },
  ],
  eir_calculator: [
    { key: "principal", label: "Principal", type: "number", default: 1000000 },
    { key: "processing_fees", label: "Processing Fees", type: "number", default: 10000 },
    { key: "annual_coupon_rate", label: "Annual Coupon Rate %", type: "number", default: 9 },
    { key: "tenure_years", label: "Tenure (years)", type: "number", default: 5 },
  ],
  consolidation: [
    { key: "parent_equity", label: "Parent — Equity", type: "number", default: 1000 },
    { key: "parent_assets", label: "Parent — Assets", type: "number", default: 2000 },
    { key: "parent_liabilities", label: "Parent — Liabilities", type: "number", default: 1000 },
    { key: "subsidiaries", label: "Subsidiaries", type: "array", default: [
      { name: "Sub A", ownership_pct: 80, equity: 500, assets: 900, liabilities: 400 },
    ], columns: [
      { key: "name", label: "Name", type: "text" },
      { key: "ownership_pct", label: "Ownership %", type: "number" },
      { key: "equity", label: "Equity", type: "number" },
      { key: "assets", label: "Assets", type: "number" },
      { key: "liabilities", label: "Liabilities", type: "number" },
    ]},
    { key: "intercompany_eliminations", label: "Inter-company Eliminations", type: "array", default: [], columns: [
      { key: "from", label: "From Entity", type: "text" },
      { key: "to", label: "To Entity", type: "text" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "description", label: "Description", type: "text" },
    ]},
  ],
  company_law_applicability: [
    { key: "paid_up_capital", label: "Paid-up Capital (₹)", type: "number", default: 0 },
    { key: "turnover", label: "Turnover (₹)", type: "number", default: 0 },
    { key: "borrowings", label: "Borrowings from Banks / PFIs (₹)", type: "number", default: 0 },
    { key: "public_deposits", label: "Public Deposits (₹)", type: "number", default: 0 },
    { key: "networth", label: "Net Worth (₹)", type: "number", default: 0 },
    { key: "net_profit_preceding_year", label: "Net Profit — Preceding FY (₹)", type: "number", default: 0 },
    { key: "security_holders", label: "No. of Security Holders", type: "number", default: 0 },
    { key: "is_listed", label: "Listed", type: "select", options: [["true","Yes"], ["false","No"]], default: "false" },
    { key: "is_private", label: "Private Co.", type: "select", options: [["true","Yes"], ["false","No"]], default: "true" },
  ],

  // ===== Advanced engines =====
  lease_modification: [
    { key: "pre_mod_liability", label: "Pre-modification Lease Liability", type: "number", default: 5000000 },
    { key: "pre_mod_rou", label: "Pre-modification ROU Asset", type: "number", default: 4800000 },
    { key: "new_term_months", label: "New Remaining Term (months)", type: "number", default: 48 },
    { key: "new_monthly_rent", label: "New Monthly Rent", type: "number", default: 110000 },
    { key: "new_ibr", label: "New IBR % p.a.", type: "number", default: 9 },
  ],
  nci_calculator: [
    { key: "fv_net_assets_acquired", label: "FV of Net Assets Acquired", type: "number", default: 100000000 },
    { key: "nci_percentage", label: "NCI %", type: "number", default: 20 },
    { key: "fv_nci_market", label: "FV of NCI (Market) — 0 = use proportionate method", type: "number", default: 0 },
  ],
  contingent_consideration: [
    { key: "discount_rate", label: "Discount Rate %", type: "number", default: 12 },
    { key: "scenarios", label: "Scenarios", type: "array", default: [
      { name: "Base", probability: 60, year: 2, amount: 5000000 },
      { name: "Upside", probability: 25, year: 3, amount: 8000000 },
      { name: "Downside", probability: 15, year: 1, amount: 2000000 },
    ], columns: [
      { key: "name", label: "Scenario", type: "text" },
      { key: "probability", label: "Probability %", type: "number" },
      { key: "year", label: "Year", type: "number" },
      { key: "amount", label: "Payout", type: "number" },
    ]},
  ],
  fx_translation: [
    { key: "closing_rate", label: "Closing Rate (₹/FX)", type: "number", default: 83.5 },
    { key: "opening_rate", label: "Opening Rate (₹/FX)", type: "number", default: 82 },
    { key: "average_rate", label: "Average Rate (₹/FX)", type: "number", default: 82.7 },
    { key: "historical_rate", label: "Historical Rate (₹/FX)", type: "number", default: 78 },
    { key: "items", label: "Trial Balance Items", type: "array", default: [
      { account: "Cash", type: "asset", foreign_amount: 100000 },
      { account: "Trade Receivables", type: "asset", foreign_amount: 250000 },
      { account: "Trade Payables", type: "liability", foreign_amount: 120000 },
      { account: "Equity", type: "equity", foreign_amount: 50000 },
      { account: "Revenue", type: "income", foreign_amount: 800000 },
      { account: "Op Expenses", type: "expense", foreign_amount: 600000 },
    ], columns: [
      { key: "account", label: "Account", type: "text" },
      { key: "type", label: "Type", type: "text" },
      { key: "foreign_amount", label: "Foreign Amount", type: "number" },
    ]},
  ],
  benford_law: [
    { key: "amounts", label: "Transaction Amounts (CSV)", type: "csv_numbers", default: "1234,1500,1812,2300,2700,3100,4200,5500,6800,8400,1100,2500,3300,4400,5500,1340,2890,3450" },
  ],
  duplicate_payment_detector: [
    { key: "payments", label: "Payments", type: "array", default: [
      { invoice_no: "INV-001", vendor: "ABC", amount: 100000 },
      { invoice_no: "INV-001", vendor: "ABC", amount: 100000 },
      { invoice_no: "INV-002", vendor: "XYZ", amount: 50000 },
    ], columns: [
      { key: "invoice_no", label: "Invoice No", type: "text" },
      { key: "vendor", label: "Vendor", type: "text" },
      { key: "amount", label: "Amount", type: "number" },
    ]},
  ],
  variance_analysis: [
    { key: "items", label: "Items", type: "array", default: [
      { name: "Revenue", actual: 10000000, budget: 9000000 },
      { name: "COGS", actual: 6500000, budget: 6000000 },
      { name: "Salaries", actual: 1500000, budget: 1400000 },
    ], columns: [
      { key: "name", label: "Line Item", type: "text" },
      { key: "actual", label: "Actual", type: "number" },
      { key: "budget", label: "Budget", type: "number" },
    ]},
  ],
  analytical_review: [
    { key: "revenue", label: "Revenue", type: "number", default: 10000000 },
    { key: "cogs", label: "COGS", type: "number", default: 6000000 },
    { key: "operating_income", label: "Operating Income", type: "number", default: 2000000 },
    { key: "pat", label: "PAT", type: "number", default: 1500000 },
    { key: "equity", label: "Equity", type: "number", default: 8000000 },
    { key: "total_assets", label: "Total Assets", type: "number", default: 20000000 },
    { key: "current_assets", label: "Current Assets", type: "number", default: 7000000 },
    { key: "current_liab", label: "Current Liabilities", type: "number", default: 5000000 },
    { key: "inventory", label: "Inventory", type: "number", default: 2000000 },
    { key: "receivables", label: "Receivables", type: "number", default: 2500000 },
    { key: "payables", label: "Payables", type: "number", default: 1800000 },
    { key: "total_debt", label: "Total Debt", type: "number", default: 5000000 },
  ],
  lead_schedule_generator: [
    { key: "trial_balance", label: "Trial Balance", type: "array", default: [
      { account: "Cash in Bank", lead_schedule: "Cash & Cash Equivalents", current_year: 1500000, prior_year: 1200000 },
      { account: "Petty Cash", lead_schedule: "Cash & Cash Equivalents", current_year: 25000, prior_year: 20000 },
      { account: "Trade Receivables", lead_schedule: "Trade Receivables", current_year: 8500000, prior_year: 7800000 },
      { account: "Inventory - RM", lead_schedule: "Inventory", current_year: 3500000, prior_year: 3200000 },
    ], columns: [
      { key: "account", label: "Account", type: "text" },
      { key: "lead_schedule", label: "Lead Schedule", type: "text" },
      { key: "current_year", label: "CY", type: "number" },
      { key: "prior_year", label: "PY", type: "number" },
    ]},
  ],
  journal_entry_testing: [
    { key: "materiality_threshold", label: "Materiality Threshold", type: "number", default: 500000 },
    { key: "journal_entries", label: "Journal Entries", type: "array", default: [
      { je_no: "JV-001", date: "2025-03-31", amount: 1500000, narration: "Year-end provision", posted_by: "admin" },
      { je_no: "JV-002", date: "2025-04-05", amount: 75000, narration: "Travel expense", posted_by: "accounts1" },
      { je_no: "JV-003", date: "2025-03-30", amount: 2000000, narration: "Round-up adjustment", posted_by: "admin" },
    ], columns: [
      { key: "je_no", label: "JE No", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "narration", label: "Narration", type: "text" },
      { key: "posted_by", label: "Posted By", type: "text" },
    ]},
  ],
  etr_reconciliation: [
    { key: "pbt", label: "Profit Before Tax", type: "number", default: 10000000 },
    { key: "statutory_rate", label: "Statutory Tax Rate %", type: "number", default: 25.17 },
    { key: "permanent_disallowances", label: "Permanent Disallowances", type: "number", default: 500000 },
    { key: "permanent_incomes_exempt", label: "Exempt Incomes", type: "number", default: 200000 },
    { key: "losses_setoff", label: "Brought-forward Losses Used", type: "number", default: 0 },
    { key: "timing_differences", label: "Timing Differences", type: "number", default: 800000 },
    { key: "current_tax_actual", label: "Actual Current Tax", type: "number", default: 2400000 },
    { key: "deferred_tax_actual", label: "Actual Deferred Tax", type: "number", default: 200000 },
  ],
  mrl_generator: [
    { key: "entity_name", label: "Entity Name", type: "text", default: "ABC Private Limited" },
    { key: "fy", label: "Financial Year", type: "text", default: "2024-25" },
    { key: "auditor_name", label: "Auditor Name", type: "text", default: "VCAJ & Associates LLP" },
  ],
  rcm_generator: [
    { key: "expenses", label: "Inward Supplies", type: "array", default: [
      { category: "legal", amount: 200000, state: "intrastate" },
      { category: "goods_transport_agency", amount: 150000, state: "intrastate" },
      { category: "director_sitting_fees", amount: 100000, state: "intrastate" },
      { category: "import_of_services", amount: 500000, state: "interstate" },
    ], columns: [
      { key: "category", label: "Category", type: "text" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "state", label: "Intrastate/Interstate", type: "text" },
    ]},
  ],
  ecb_calculator: [
    { key: "principal_amount", label: "Principal Amount (USD)", type: "number", default: 5000000 },
    { key: "tenure_years", label: "Tenure (years)", type: "number", default: 5 },
    { key: "interest_rate_pct", label: "Interest Rate %", type: "number", default: 7.5 },
    { key: "benchmark_rate_pct", label: "Benchmark Rate (SOFR/EURIBOR) %", type: "number", default: 4 },
  ],
  fc_gpr: [
    { key: "trigger", label: "Trigger (FDI / Rights / Bonus etc.)", type: "text", default: "FDI Inflow" },
  ],
  fc_trs: [
    { key: "trigger", label: "Trigger", type: "text", default: "Resident-to-Non-Resident transfer" },
  ],
  odi_check: [
    { key: "trigger", label: "Trigger", type: "text", default: "Overseas equity investment" },
  ],
  fdi_check: [
    { key: "sector", label: "Sector", type: "text", default: "Manufacturing" },
  ],
  audit_query_tracker: [
    { key: "queries", label: "Queries", type: "array", default: [
      { sno: 1, query: "Provide ageing of trade receivables", raised_on: "2025-04-15", status: "Open" },
      { sno: 2, query: "Confirm related party transactions list", raised_on: "2025-04-16", status: "Closed" },
    ], columns: [
      { key: "sno", label: "SNo", type: "number" },
      { key: "query", label: "Query", type: "text" },
      { key: "raised_on", label: "Raised On", type: "text" },
      { key: "status", label: "Status", type: "text" },
    ]},
  ],
  loan_modification: [
    { key: "pv_new_terms", label: "PV of New Terms (using ORIGINAL EIR)", type: "number", default: 9500000 },
    { key: "pv_old_terms", label: "PV of Old Terms (carrying amount)", type: "number", default: 10000000 },
  ],
  customer_relationship_mpeem: [
    { key: "revenue_year_1", label: "Year 1 Revenue from Customer Base", type: "number", default: 50000000 },
    { key: "growth_rate_pct", label: "Revenue Growth %", type: "number", default: 5 },
    { key: "attrition_rate_pct", label: "Customer Attrition %", type: "number", default: 8 },
    { key: "ebitda_margin_pct", label: "EBITDA Margin %", type: "number", default: 20 },
    { key: "contributory_charge_pct", label: "Contributory Asset Charge %", type: "number", default: 8 },
    { key: "tax_rate_pct", label: "Tax Rate %", type: "number", default: 25.17 },
    { key: "discount_rate_pct", label: "Discount Rate %", type: "number", default: 14 },
    { key: "horizon_years", label: "Projection Horizon (years)", type: "number", default: 7 },
  ],
  brand_relief_from_royalty: [
    { key: "revenue_year_1", label: "Year 1 Revenue", type: "number", default: 100000000 },
    { key: "growth_rate_pct", label: "Revenue Growth %", type: "number", default: 5 },
    { key: "royalty_rate_pct", label: "Royalty Rate %", type: "number", default: 3 },
    { key: "tax_rate_pct", label: "Tax Rate %", type: "number", default: 25.17 },
    { key: "discount_rate_pct", label: "Discount Rate %", type: "number", default: 12 },
    { key: "horizon_years", label: "Projection Horizon (years)", type: "number", default: 7 },
  ],
};

// Default fallback for stub engines
export const STUB_SCHEMA = [
  { key: "notes", label: "Notes / Description", type: "text", default: "" },
];

export function getSchema(toolId) {
  return TOOL_SCHEMAS[toolId] || STUB_SCHEMA;
}
